/**
 * Patient Prescriptions list.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { prescriptionAPI } from '../../services/api';
import Layout from '../../components/Layout';
import toast from 'react-hot-toast';
import { useNotifications } from '../../contexts/NotificationsContext';

const PatientPrescriptions = () => {
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const { on } = useNotifications();

  const loadPrescriptions = useCallback(async () => {
    try {
      const res = await prescriptionAPI.list();
      setPrescriptions(Array.isArray(res.data) ? res.data : (res.data?.prescriptions || []));
    } catch {
      toast.error('Failed to load prescriptions.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPrescriptions();
  }, [loadPrescriptions]);

  useEffect(() => on('prescription_ready', loadPrescriptions), [on, loadPrescriptions]);
  useEffect(() => on('prescription_updated', loadPrescriptions), [on, loadPrescriptions]);

  return (
    <Layout title="Prescriptions" role="patient" showBack>
      <div className="p-4 pb-24">
        {loading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => <div key={i} className="skeleton h-24 rounded-2xl" />)}
          </div>
        ) : prescriptions.length === 0 ? (
          <div className="card text-center py-12 text-gray-400">
            <svg className="w-16 h-16 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="font-medium">No prescriptions yet</p>
            <p className="text-sm mt-1">Prescriptions from your consultations will appear here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {prescriptions.map(rx => (
              <Link key={rx.id} to={`/patient/prescriptions/${rx.id}`} className="card hover:shadow-md transition-shadow">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">{rx.diagnosis || 'Prescription'}</p>
                    <p className="text-sm text-gray-500">Dr. {rx.doctor_name || 'Unknown'}</p>
                    <p className="text-xs text-gray-400 mt-1">{rx.created_at ? new Date(rx.created_at).toLocaleDateString() : ''}</p>
                    {rx.medications && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {(Array.isArray(rx.medications) ? rx.medications : [rx.medications]).slice(0, 3).map((med, i) => (
                          <span key={i} className="px-2 py-0.5 bg-purple-50 text-purple-700 text-xs rounded-full">
                            {typeof med === 'string' ? med : med.name || 'Medication'}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <svg className="w-4 h-4 text-gray-400 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PatientPrescriptions;
